// Shared note timestamp parser. Notes were imported from RestorePM in two
// different shapes:
//
//   1. Modern shape: `created_on: "5/19/2026 4:57pm"`, `text: "Sent F/U..."`
//   2. Legacy shape: `created_on: "2026-05-11"` (date only),
//                    `text: "Kelita (2026-05-11 10:17:50) - I spoke to..."`
//      — the real timestamp is buried inside the text prefix.
//
// Sorting "latest note first" on the boards has to consider BOTH shapes,
// otherwise a legacy note dated 5/11 (read as midnight) outranks a modern
// note from 5/19 4:57pm whose `Date.parse` returned NaN.
const EMBEDDED_RE = /\((\d{4}-\d{2}-\d{2}(?:[ T]\d{2}:\d{2}(?::\d{2})?)?)\)\s*[-–:]/;

/**
 * Return a millisecond timestamp for a note, or 0 if unparseable.
 * Prefers the embedded `Name (YYYY-MM-DD HH:MM:SS) -` prefix in the text
 * field when present (more precise than the bare `created_on` date), and
 * falls back to a forgiving parser for both ISO and "M/D/YYYY h:mma" shapes
 * on `created_on`.
 */
export function noteTimestamp(note) {
  if (!note) return 0;
  // New atomic notes store a canonical UTC ISO timestamp. Prefer it over the
  // legacy friendly display string so each browser can use its own timezone.
  const createdAt = String(note.created_at || '').trim();
  if (createdAt) {
    const d = new Date(createdAt);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  // Check the embedded timestamp first — it's the authoritative time.
  const text = String(note.text || '');
  const m = text.match(EMBEDDED_RE);
  if (m) {
    const d = new Date(m[1].replace(' ', 'T'));
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }

  const raw = String(note.created_on || '').trim();
  if (!raw) return 0;

  // ISO-ish ("2026-05-11" or "2026-05-11 10:17:50" or "2026-05-11T10:17:50")
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const d = new Date(raw.replace(' ', 'T'));
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }

  // "M/D/YYYY h:mma" or "M/D/YYYY h:mm am"
  const friendly = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(am|pm)?)?$/i);
  if (friendly) {
    const [, mo, da, yr, hr, mi, ap] = friendly;
    let h = hr ? parseInt(hr, 10) : 0;
    if (ap && ap.toLowerCase() === 'pm' && h < 12) h += 12;
    if (ap && ap.toLowerCase() === 'am' && h === 12) h = 0;
    return new Date(parseInt(yr, 10), parseInt(mo, 10) - 1, parseInt(da, 10), h, mi ? parseInt(mi, 10) : 0).getTime();
  }

  // Last resort — let the native parser try.
  const fallback = Date.parse(raw);
  return Number.isNaN(fallback) ? 0 : fallback;
}

/**
 * Format an atomic note's UTC timestamp in the viewer's local timezone.
 * Legacy imported/client-authored notes have no created_at and retain their
 * existing human-readable created_on value unchanged.
 */
export function formatNoteTimestamp(note) {
  const createdAt = String(note?.created_at || '').trim();
  if (createdAt) {
    const date = new Date(createdAt);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString('en-US', {
        month: 'numeric', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      }).toLowerCase().replace(',', '');
    }
  }
  return String(note?.created_on || '').trim();
}

export function isRecentlySavedNote(note, now = Date.now()) {
  const createdAt = String(note?.created_at || '').trim();
  if (!createdAt) return false;
  const savedAt = new Date(createdAt).getTime();
  return !Number.isNaN(savedAt) && savedAt <= now && now - savedAt < 2 * 60 * 1000;
}
